import React, { useEffect, useRef, useState } from "react";
import { X, Clock, BookOpen, Check } from "lucide-react";
import api from "@/lib/api";
import ChapterBlocks from "@/components/ChapterBlocks";

export default function ChapterReader({ chapter, progress, onClose, onProgressUpdate }) {
  const [seconds, setSeconds] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (!chapter) return;
    startRef.current = Date.now();
    const t = setInterval(() => setSeconds(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [chapter]);

  if (!chapter) return null;

  const alreadyCompleted = !!progress?.chapters?.[chapter.id]?.completed;
  const totalSeconds = (progress?.chapters?.[chapter.id]?.time_seconds || 0) + seconds;

  const markComplete = async () => {
    const time_seconds = Math.floor((Date.now() - startRef.current) / 1000);
    const { data } = await api.post("/progress/chapter", {
      chapter_id: chapter.id,
      time_seconds,
      completed: true,
    });
    onProgressUpdate?.(data);
    onClose();
  };

  const closeAndSave = async () => {
    const time_seconds = Math.floor((Date.now() - startRef.current) / 1000);
    if (time_seconds > 2) {
      try {
        const { data } = await api.post("/progress/chapter", {
          chapter_id: chapter.id,
          time_seconds,
          completed: alreadyCompleted,
        });
        onProgressUpdate?.(data);
      } catch (err) {
        // Best-effort progress sync. Don't block the user from closing the
        // chapter, but log so we can spot persistent issues in the console.
        console.warn("Could not save chapter progress:", err?.response?.status);
      }
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/70 backdrop-blur-sm overflow-y-auto" data-testid="chapter-reader-modal">
      <div className="min-h-screen flex items-start md:items-center justify-center p-4 md:p-12">
        <div className="bg-[var(--bloom-bg)] max-w-3xl w-full relative bloom-rise shadow-2xl">
          {/* Top bar */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-8 md:px-14 py-5 border-b border-[var(--bloom-border)] bg-[var(--bloom-bg)]">
            <div className="flex items-center gap-4 text-xs text-[var(--bloom-ink-muted)] tracking-[0.15em] uppercase">
              <span>Chapter {String(chapter.number).padStart(2, "0")} / 15</span>
              <span className="flex items-center gap-1"><Clock size={12} /> {formatTime(totalSeconds)}</span>
              {alreadyCompleted && <span className="flex items-center gap-1 text-[var(--bloom-brand)]"><Check size={12} /> Read</span>}
            </div>
            <button onClick={closeAndSave} className="bloom-btn bloom-btn-ghost" data-testid="chapter-close-btn">
              <X size={16} /> Close
            </button>
          </div>

          {/* Content */}
          <article className="px-8 md:px-16 py-12 md:py-16">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-6">
              ✦ Founding Grower Prospectus ✦
            </div>
            <div className="bloom-chapter-number mb-2">{String(chapter.number).padStart(2, "0")}</div>
            <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] mb-3" data-testid="chapter-reader-title">{chapter.title}</h1>
            <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)] mb-10">{chapter.subtitle}</p>

            <div className="bloom-divider mb-10">
              <BookOpen size={14} className="opacity-50" />
              {chapter.read_minutes} min read
            </div>

            {chapter.blocks && chapter.blocks.length > 0 ? (
              <ChapterBlocks blocks={chapter.blocks} />
            ) : (
              <div className="bloom-prose">
                {chapter.body.split("\n\n").map((para, i) => (
                  <p key={i}>{para.trim()}</p>
                ))}
              </div>
            )}

            <div className="bloom-divider my-12">✦ end of chapter ✦</div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between items-center">
              <button onClick={closeAndSave} className="bloom-btn bloom-btn-outline" data-testid="chapter-keep-reading-btn">
                Keep Reading Later
              </button>
              <button onClick={markComplete} className="bloom-btn bloom-btn-primary" data-testid="chapter-mark-complete-btn">
                {alreadyCompleted ? "Re-confirm Read" : "Mark Chapter Complete"}
              </button>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

function formatTime(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m === 0) return `${r}s`;
  return `${m}m ${r}s`;
}
